import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL, BACKEND_URL, formatDate, UserRoles } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Plus, MoreHorizontal, Pencil, Trash2, BookOpen, Search, Loader2, Clock, Upload, X, PenLine, Building2, Check } from 'lucide-react';

const Courses = () => {
  const { user } = useAuth();
  usePageTitle('Courses');
  const canManageCourses = user?.role === UserRoles.ADMIN || user?.role === UserRoles.TRAINING_ADMIN;
  
  const [courses, setCourses] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [trainingCenters, setTrainingCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    certificate_prefix: '',
    expiration_years: '',  // Empty string for no expiration
    default_hours: 8,
    certificate_template_id: '',
    training_center_ids: [],
    outcomes: []
  });

  const fetchData = async () => {
    try {
      const [coursesRes, templatesRes, centersRes] = await Promise.all([
        axios.get(`${API_URL}/courses`),
        axios.get(`${API_URL}/certificate-templates`),
        axios.get(`${API_URL}/training-centers`)
      ]);
      setCourses(coursesRes.data);
      setTemplates(templatesRes.data);
      setTrainingCenters(centersRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.certificate_prefix) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    
    try {
      const payload = {
        ...formData,
        expiration_years: formData.expiration_years ? parseInt(formData.expiration_years) : null,
        certificate_template_id: formData.certificate_template_id || null,
        training_center_ids: formData.training_center_ids || [],
        outcomes: (formData.outcomes || []).map(o => o.trim()).filter(Boolean)
      };
      
      if (editingCourse) {
        await axios.put(`${API_URL}/courses/${editingCourse.id}`, payload);
        toast.success('Course updated successfully');
      } else {
        await axios.post(`${API_URL}/courses`, payload);
        toast.success('Course created successfully');
      }
      
      setDialogOpen(false);
      setEditingCourse(null);
      setFormData({ name: '', certificate_prefix: '', expiration_years: '', default_hours: 8, certificate_template_id: '', training_center_ids: [], outcomes: [] });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save course');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (course) => {
    setEditingCourse(course);
    setFormData({
      name: course.name,
      certificate_prefix: course.certificate_prefix,
      expiration_years: course.expiration_years || '',  // Empty if no expiration
      default_hours: course.default_hours || 8,
      certificate_template_id: course.certificate_template_id || '',
      training_center_ids: course.training_center_ids || [],
      outcomes: course.outcomes || []
    });
    setDialogOpen(true);
  };

  const handleDelete = async (course) => {
    setDeleteConfirm(course);
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    
    const course = deleteConfirm;
    setDeleteConfirm(null);
    
    try {
      await axios.delete(`${API_URL}/courses/${course.id}`);
      toast.success('Course deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete course');
    }
  };

  const getTemplateName = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    return template?.name || '-';
  };

  const getTrainingCenterNames = (centerIds) => {
    if (!centerIds || centerIds.length === 0) return [];
    return centerIds.map(id => {
      const center = trainingCenters.find(tc => tc.id === id);
      return center?.name || id;
    });
  };

  const toggleTrainingCenter = (centerId) => {
    setFormData(prev => {
      const currentIds = prev.training_center_ids || [];
      if (currentIds.includes(centerId)) {
        return { ...prev, training_center_ids: currentIds.filter(id => id !== centerId) };
      } else {
        return { ...prev, training_center_ids: [...currentIds, centerId] };
      }
    });
  };

  const filteredCourses = courses.filter(course =>
    course.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    course.certificate_prefix.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-slate-200 rounded w-48" />
        <div className="h-96 bg-slate-200 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="courses-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Courses
          </h1>
          <p className="text-slate-500 mt-1">
            Manage training courses and certificate settings
          </p>
        </div>
        {canManageCourses && (
          <Button 
            className="bg-[#2C9DE1] hover:bg-[#1A7BB8] text-white"
            onClick={() => {
              setEditingCourse(null);
              setFormData({ name: '', certificate_prefix: '', expiration_years: '', default_hours: 8, certificate_template_id: '' });
              setDialogOpen(true);
            }}
            data-testid="add-course-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Course
          </Button>
        )}
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search courses..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="course-search-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Courses Table */}
      <Card>
        <CardContent className="p-0">
          {filteredCourses.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-900">No courses found</h3>
              <p className="text-slate-500 mt-1">
                {searchTerm ? 'Try a different search term' : 'Get started by adding your first course'}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold">Course Name</TableHead>
                  <TableHead className="font-semibold">Certificate Prefix</TableHead>
                  <TableHead className="font-semibold">Training Centers</TableHead>
                  <TableHead className="font-semibold">Default Hours</TableHead>
                  <TableHead className="font-semibold">Expiration</TableHead>
                  <TableHead className="font-semibold">Print Template</TableHead>
                  <TableHead className="font-semibold">Next Cert #</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCourses.map((course) => (
                  <TableRow key={course.id} className="hover:bg-slate-50/50" data-testid={`course-row-${course.id}`}>
                    <TableCell className="font-medium">{course.name}</TableCell>
                    <TableCell>
                      <span className="font-mono text-sm bg-slate-100 px-2 py-1 rounded">
                        {course.certificate_prefix}
                      </span>
                    </TableCell>
                    <TableCell>
                      {course.training_center_ids?.length > 0 ? (
                        <div className="flex flex-wrap gap-1 max-w-[200px]">
                          {getTrainingCenterNames(course.training_center_ids).map((name, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                              {name}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <span className="text-slate-400 text-sm">All centers</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4 text-slate-400" />
                        {course.default_hours || 8}h
                      </div>
                    </TableCell>
                    <TableCell>
                      {course.expiration_years 
                        ? `${course.expiration_years} year${course.expiration_years > 1 ? 's' : ''}`
                        : <span className="text-slate-400">No expiration</span>
                      }
                    </TableCell>
                    <TableCell>
                      {course.certificate_template_id ? (
                        <Badge className="bg-emerald-100 text-emerald-800">
                          {getTemplateName(course.certificate_template_id)}
                        </Badge>
                      ) : (
                        <span className="text-slate-400 text-sm">Not assigned</span>
                      )}
                    </TableCell>
                    <TableCell className="font-mono">{course.next_certificate_number}</TableCell>
                    <TableCell>
                      {canManageCourses && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" data-testid={`course-actions-${course.id}`}>
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleEdit(course)}>
                              <Pencil className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => handleDelete(course)}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              {editingCourse ? 'Edit Course' : 'Add New Course'}
            </DialogTitle>
            <DialogDescription>
              {editingCourse ? 'Update course details and training centers' : 'Create a new training course'}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Course Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Emergency First Aid at Work"
                data-testid="course-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="prefix">Certificate Prefix *</Label>
              <Input
                id="prefix"
                value={formData.certificate_prefix}
                onChange={(e) => setFormData({ ...formData, certificate_prefix: e.target.value.toUpperCase() })}
                placeholder="e.g., EFW"
                maxLength={5}
                data-testid="course-prefix-input"
              />
              <p className="text-xs text-slate-500">
                This prefix will be used for certificate numbers (e.g., EFW00001)
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="default_hours">Default Training Hours *</Label>
                <Input
                  id="default_hours"
                  type="number"
                  min={1}
                  max={100}
                  value={formData.default_hours}
                  onChange={(e) => setFormData({ ...formData, default_hours: parseInt(e.target.value) || 8 })}
                  data-testid="course-hours-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="expiration">Expiration (Years)</Label>
                <Input
                  id="expiration"
                  type="number"
                  min={1}
                  max={10}
                  placeholder="Leave empty for no expiration"
                  value={formData.expiration_years}
                  onChange={(e) => setFormData({ ...formData, expiration_years: e.target.value ? parseInt(e.target.value) : '' })}
                  data-testid="course-expiration-input"
                />
                <p className="text-xs text-slate-500">Leave empty if certificates do not expire</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="template">Certificate Print Template</Label>
              <Select 
                value={formData.certificate_template_id} 
                onValueChange={(value) => setFormData({ ...formData, certificate_template_id: value === 'none' ? '' : value })}
              >
                <SelectTrigger data-testid="course-template-select">
                  <SelectValue placeholder="Select a print template (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No template</SelectItem>
                  {templates.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                Configure field positions for printing certificates on pre-printed paper
              </p>
            </div>

            {/* Alternate Certificate Outcomes */}
            <div className="space-y-2">
              <Label>Alternate Certificate Outcomes</Label>
              <p className="text-xs text-slate-500">
                Optional. If a class can result in different certificate names (e.g. &quot;Pool Lifeguard&quot; vs &quot;Pool Attendant&quot;), list the alternates here. The same prefix and number sequence is shared. The course&apos;s primary name ({formData.name || 'this course'}) is always available — only add alternates.
              </p>
              <div className="space-y-2">
                {(formData.outcomes || []).map((outcome, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Input
                      value={outcome}
                      onChange={(e) => {
                        const next = [...formData.outcomes];
                        next[idx] = e.target.value;
                        setFormData({ ...formData, outcomes: next });
                      }}
                      placeholder="e.g. Pool Attendant"
                      data-testid={`course-outcome-input-${idx}`}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => {
                        setFormData({
                          ...formData,
                          outcomes: formData.outcomes.filter((_, i) => i !== idx)
                        });
                      }}
                      data-testid={`course-outcome-remove-${idx}`}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setFormData({ ...formData, outcomes: [...(formData.outcomes || []), ''] })}
                  data-testid="course-outcome-add-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add alternate outcome
                </Button>
              </div>
            </div>

            {/* Training Centers Assignment */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                Assigned Training Centers
              </Label>
              <div className="border rounded-lg p-3 max-h-[150px] overflow-y-auto">
                {trainingCenters.length === 0 ? (
                  <p className="text-sm text-slate-500">No training centers available</p>
                ) : (
                  <div className="space-y-2">
                    {trainingCenters.map((center) => (
                      <div 
                        key={center.id}
                        className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                          formData.training_center_ids?.includes(center.id) 
                            ? 'bg-blue-50 border border-blue-200' 
                            : 'hover:bg-slate-50 border border-transparent'
                        }`}
                        onClick={() => toggleTrainingCenter(center.id)}
                      >
                        <div className={`w-5 h-5 rounded border flex items-center justify-center ${
                          formData.training_center_ids?.includes(center.id)
                            ? 'bg-blue-600 border-blue-600'
                            : 'border-slate-300'
                        }`}>
                          {formData.training_center_ids?.includes(center.id) && (
                            <Check className="w-3 h-3 text-white" />
                          )}
                        </div>
                        <span className="text-sm">{center.name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <p className="text-xs text-slate-500">
                Select which training centers can offer this course. Leave empty to allow all centers.
              </p>
            </div>

            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-[#2C9DE1] hover:bg-[#1A7BB8] text-white"
                disabled={saving}
                data-testid="course-submit-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  editingCourse ? 'Update Course' : 'Create Course'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <Dialog open={true} onOpenChange={() => setDeleteConfirm(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                Delete Course
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to delete <strong>{deleteConfirm.name}</strong>? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex gap-2 mt-4">
              <Button
                variant="outline"
                className="bg-[#2C9DE1] hover:bg-[#1A7BB8] text-white border-0"
                onClick={() => setDeleteConfirm(null)}
              >
                Cancel
              </Button>
              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={confirmDelete}
              >
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default Courses;
